import classNames from 'classnames';
import React, {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  forwardRef,
  Ref,
  useState,
} from 'react';
import { formatString, makeId } from '../../../utilities/idComponent';
import styles from './Button.module.css';

export enum ButtonType {
  Filled = 'filled',
  Outlined = 'outlined',
  Text = 'text',
}

export enum ButtonSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export enum ButtonColor {
  Primary = 'primary',
  Secondary = 'secondary',
  Destructive = 'destructive',
  WhatsApp = 'whatsapp',
  CustomTheme = 'custom-theme',
  CustomThemeOutlined = 'custom-theme-outlined',
  Banner = 'banner',
}

export type ButtonProps = {
  /**  Will be mandatory. use PascalCase to describe e.g ApprovePayment*/
  name?: string;
  /** ButtonType Filled | Outlined | Text */
  buttonType?: ButtonType;
  /** ButtonSize Small | Medium \ Large */
  size?: ButtonSize;
  /** ButtonColor Primary | Secondary | Destructive */
  color?: ButtonColor;
  fullWidth?: boolean;
  /** Allow dedupe mechanism, default is false */
  isDedupe?: boolean;
  dedupeValue?: number;
  useMinWidth?: boolean;
} & DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      fullWidth,
      useMinWidth,
      id,
      name,
      buttonType = ButtonType.Filled,
      color = ButtonColor.Primary,
      size = ButtonSize.Medium,
      isDedupe = false,
      dedupeValue = 500,
      ...props
    }: ButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) => {
    const classes = classNames(
      styles[buttonType],
      styles[size],
      styles[color],
      {
        [styles.fullWidth]: fullWidth,
      },
      {
        [styles.useMinWidth]: useMinWidth,
      },
    );
    const [isPressed, setIsPressed] = useState(false);

    const eventClasses = classNames({
      [styles.pressed]: isPressed,
    });

    const handleMouseDown = () => {
      setIsPressed(true);
    };
    const handleMouseUp = () => {
      setIsPressed(false);
    };

    const buttonId = makeId('btn', name ?? formatString(props.children), id);

    const [isDisabled, setIsDisabled] = useState(false);

    const handleClick = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      const runOnClick = () => {
        if (props.onClick) {
          props.onClick(e);
        }
      };

      // Only execute function after timeout is done
      if (isDedupe) {
        runOnClick();
        setIsDisabled(true);
        setTimeout(() => {
          setIsDisabled(false);
        }, dedupeValue); // Adjust the cooldown period as needed
      } else {
        runOnClick();
      }
    };

    const isButtonDisabled = () => {
      return props.disabled || isDisabled;
    };

    return (
      <button
        {...props}
        title={name ?? props.title}
        id={buttonId}
        ref={ref}
        disabled={isButtonDisabled()}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleClick}
        className={classNames(
          styles.buttonBase,
          styles.button,
          classes,
          eventClasses,
          className,
        )}
      />
    );
  },
);

export default Object.assign(Button, {
  Size: ButtonSize,
  Type: ButtonType,
  Color: ButtonColor,
});
